import { z } from 'zod';
import { CLAIM_STATUS, ClaimStatusValue, baseClaimSchema } from './baseClaim';
import {
  CLAIM_TARIFF_STATUS,
  ClaimTariffStatusValue,
  tariffSchema,
} from './tariff';

type ClaimStatusRollupClaim = Partial<
  Pick<z.infer<typeof baseClaimSchema>, 'status'>
>;
type ClaimStatusRollupTariff = Pick<
  z.infer<typeof tariffSchema>,
  'id' | 'status'
>;

export interface ComputeClaimStatusFromTariffsInput {
  claim?: ClaimStatusRollupClaim;
  tariffs: ClaimStatusRollupTariff[];
  options?: {
    forceCancelled?: boolean;
  };
}

const submittedRollupStatuses = new Set<ClaimTariffStatusValue>([
  CLAIM_TARIFF_STATUS.SUBMITTED,
  CLAIM_TARIFF_STATUS.PENDING,
  CLAIM_TARIFF_STATUS.QUERIED,
  CLAIM_TARIFF_STATUS.PAYOR_INVOICED,
]);

const paidRollupStatuses = new Set<ClaimTariffStatusValue>([
  CLAIM_TARIFF_STATUS.PAID,
  CLAIM_TARIFF_STATUS.OVERPAID,
]);

const partiallyPaidAllowedStatuses = new Set<ClaimTariffStatusValue>([
  CLAIM_TARIFF_STATUS.PAID,
  CLAIM_TARIFF_STATUS.OVERPAID,
  CLAIM_TARIFF_STATUS.UNDERPAID,
  CLAIM_TARIFF_STATUS.WRITTEN_OFF,
]);

const terminalRollupStatuses = new Set<ClaimTariffStatusValue>([
  CLAIM_TARIFF_STATUS.PAID,
  CLAIM_TARIFF_STATUS.OVERPAID,
  CLAIM_TARIFF_STATUS.UNDERPAID,
  CLAIM_TARIFF_STATUS.WRITTEN_OFF,
  CLAIM_TARIFF_STATUS.CANCELLED,
]);

export const isSubmittedTariffRollupStatus = (
  status: ClaimTariffStatusValue
): boolean => submittedRollupStatuses.has(status);

export const isPaidTariffRollupStatus = (
  status: ClaimTariffStatusValue
): boolean => paidRollupStatuses.has(status);

export const isTerminalTariffRollupStatus = (
  status: ClaimTariffStatusValue
): boolean => terminalRollupStatuses.has(status);

type ClaimTariffStatusDisplayMapper = (
  status: ClaimTariffStatusValue
) => string;

const getClerkTariffStatusDisplay: ClaimTariffStatusDisplayMapper = (status) =>
  status;

export const rollUpClaimTariffStatuses = (
  statuses: readonly ClaimTariffStatusValue[],
  getStatusDisplay: ClaimTariffStatusDisplayMapper = getClerkTariffStatusDisplay
): string => {
  const statusesToCount = statuses.every(
    (status) => status === CLAIM_TARIFF_STATUS.UNCLAIMABLE
  )
    ? statuses
    : statuses.filter((status) => status !== CLAIM_TARIFF_STATUS.UNCLAIMABLE);
  const distinctStatuses = new Set(
    statusesToCount
      .map(getStatusDisplay)
      .map((status) => status.trim())
      .filter(Boolean)
  );

  if (distinctStatuses.size === 0) {
    return (
      getStatusDisplay(CLAIM_TARIFF_STATUS.INCOMPLETE).trim() ||
      CLAIM_TARIFF_STATUS.INCOMPLETE
    );
  }

  if (distinctStatuses.size === 1) {
    return (
      distinctStatuses.values().next().value ?? CLAIM_TARIFF_STATUS.INCOMPLETE
    );
  }

  return `Mixed (${distinctStatuses.size})`;
};

const areAllStatuses = (
  statuses: ClaimTariffStatusValue[],
  target: ClaimTariffStatusValue
): boolean =>
  statuses.length > 0 && statuses.every((status) => status === target);

const areAllInSet = (
  statuses: ClaimTariffStatusValue[],
  allowed: Set<ClaimTariffStatusValue>
): boolean =>
  statuses.length > 0 && statuses.every((status) => allowed.has(status));

export const computeClaimStatusFromTariffs = ({
  tariffs,
  options,
}: ComputeClaimStatusFromTariffsInput): ClaimStatusValue => {
  const statuses = tariffs.map((tariff) => tariff.status);

  if (
    options?.forceCancelled ||
    areAllStatuses(statuses, CLAIM_TARIFF_STATUS.CANCELLED)
  ) {
    return CLAIM_STATUS.CANCELLED;
  }

  if (areAllStatuses(statuses, CLAIM_TARIFF_STATUS.WRITTEN_OFF)) {
    return CLAIM_STATUS.WRITTEN_OFF;
  }

  if (areAllInSet(statuses, paidRollupStatuses)) {
    return CLAIM_STATUS.PAID;
  }

  if (areAllInSet(statuses, partiallyPaidAllowedStatuses)) {
    const hasPaidLike = statuses.some((status) =>
      isPaidTariffRollupStatus(status)
    );
    const hasNonPaidLike = statuses.some(
      (status) => !isPaidTariffRollupStatus(status)
    );
    if (hasPaidLike && hasNonPaidLike) {
      return CLAIM_STATUS.PARTIALLY_PAID;
    }
  }

  if (areAllInSet(statuses, submittedRollupStatuses)) {
    return CLAIM_STATUS.SUBMITTED;
  }

  return CLAIM_STATUS.IN_PROGRESS;
};
